import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { theme } from '../styles/theme';
import { Link } from 'react-router-dom';
import { NavigationButton as BaseNavigationButton } from '../styles';
import LazyImage from './LazyImage';

const SliderContainer = styled.div`
  position: relative;
  padding: 0;
  overflow: visible;
  background-image: url('/images/white-to-blue-wave.svg');
  background-color: white;
  background-repeat: no-repeat;
  background-position: bottom;
  background-size: 100% auto;

  width: 100%;
  margin: 0 auto;
  display: flex;
  flex-direction: column;
  justify-content: center;
  
  @media (max-width: ${theme.breakpoints.tablet}) {
    height: 400px;
  }
  
  @media (max-width: ${theme.breakpoints.mobile}) {
    height: 220px;
  }
`;

const SliderWrapper = styled.div`
  width: 100%;
  height: auto;
  position: relative;
  padding-bottom: 40px; /* インジケーター用の余白 */
  display: flex;
  flex-direction: column;
  justify-content: center;
  
  @media (max-width: ${theme.breakpoints.mobile}) {
    padding-bottom: 30px;
  }
`;

const SliderTrack = styled.div<{ activeIndex: number; totalImages: number }>`
  display: flex;
  height: 100%;
  transform: ${props => `translateX(calc(-${props.activeIndex + 1} * 65vw + 17vw))`};
  transition: transform 0.5s ease;
  align-items: center;
`;

const SlideItemContainer = styled.div<{ isActive: boolean }>`
  flex: 0 0 auto;
  width: 65vw;
  height: 100%;
  padding: 0 10px;
  position: relative;
  cursor: pointer;
  transition: all 0.5s ease;
  display: flex;
  align-items: center;
  justify-content: center;
  align-self: center;
`;

const ImageWrapper = styled.div`
  width: 100%;
  max-width: 900px; /* 最大幅を設定 */
  position: relative;
  overflow: hidden;
  margin: 0 auto;
  height: auto; /* 高さを自動調整に変更 */
`;

const AspectRatioBox = styled.div`
  position: relative;
  width: 100%;
  padding-top: 56.25%; /* 16:9アスペクト比を維持 */
  overflow: hidden;
  border-radius: 48px;
  
  @media (max-width: ${theme.breakpoints.tablet}) {
    border-radius: 36px;
  }
  
  @media (max-width: ${theme.breakpoints.mobile}) {
    border-radius: 24px;
  }
`;

const SlideItemLink = styled(Link)<{ $backgroundImage: string; $isActive: boolean }>`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  text-decoration: none;
  display: block;
  box-shadow: ${props => props.$isActive ? theme.shadows.md : 'none'};
  border-radius: 48px;
  
  @media (max-width: ${theme.breakpoints.tablet}) {
    border-radius: 36px;
  }
  
  @media (max-width: ${theme.breakpoints.mobile}) {
    border-radius: 24px;
  }
`;

const SlideItemDiv = styled.div<{ $backgroundImage: string; $isActive: boolean }>`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  box-shadow: ${props => props.$isActive ? theme.shadows.md : 'none'};
  border-radius: 48px;
  
  @media (max-width: ${theme.breakpoints.tablet}) {
    border-radius: 36px;
  }
  
  @media (max-width: ${theme.breakpoints.mobile}) {
    border-radius: 24px;
  }
`;

const StyledLazyImage = styled(LazyImage)`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  border-radius: 48px;
  
  img {
    object-fit: cover;
    object-position: center;
    border-radius: 48px;
  }
  
  @media (max-width: ${theme.breakpoints.tablet}) {
    border-radius: 36px;
    
    img {
      border-radius: 36px;
    }
  }
  
  @media (max-width: ${theme.breakpoints.mobile}) {
    border-radius: 24px;
    
    img {
      border-radius: 24px;
    }
  }
`;

const CustomNavigationButton = styled(BaseNavigationButton)`
  &.prev, &.next {
    position: absolute;
    top: 50%;
    transform: translateY(-50%);
    z-index: 20;
    font-size: 1.5rem;
    background-color: rgba(255, 255, 255, 0.7);
    border-radius: 50%;
    width: 50px;
    height: 50px;
    display: flex;
    align-items: center;
    justify-content: center;
    box-shadow: ${theme.shadows.sm};
    
    @media (max-width: ${theme.breakpoints.tablet}) {
      width: 30px;
      height: 30px;
      font-size: 1.2rem;
    }
    
    @media (max-width: ${theme.breakpoints.mobile}) {
      width: 20px;
      height: 20px;
      font-size: 1rem;
      top: calc(50% - 10px);
    }
  }
  
  &.prev {
    left: calc(15vw - 0px);
    
    @media (max-width: ${theme.breakpoints.mobile}) {
      left: calc(15vw - 0px);
    }
  }
  
  &.next {
    right: calc(15vw - 0px);
    
    @media (max-width: ${theme.breakpoints.mobile}) {
      right: calc(15vw - 0px);
    }
  }
`;

const IndicatorContainer = styled.div`
  display: flex;
  justify-content: center;
  gap: ${theme.space.sm};
  position: absolute;
  bottom: 0;
  left: 50%;
  transform: translateX(-50%);
  z-index: 10;
  background-color: transparent;
  border-radius: 20px;
  padding: 8px 16px;
  
  @media (max-width: ${theme.breakpoints.mobile}) {
    padding: 16px 8px;
  }
`;

const Indicator = styled.button<{ active?: boolean }>`
  width: 12px;
  height: 12px;
  border: none;
  border-radius: 50%;
  background-color: ${props => (props.active ? '#e3f2fd' : theme.colors.primaryDark)};
  cursor: pointer;
  transition: background-color 0.3s;
  padding: 0;
  margin: 0 4px;
  
  &:hover {
    background-color: ${props => props.active ? '#e3f2fd' : theme.colors.primaryDark};
  }
`;

interface ImageSliderProps {
  images: readonly string[];
  autoPlayInterval?: number;
  links?: readonly string[];
}

const ImageSlider: React.FC<ImageSliderProps> = ({ 
  images, 
  autoPlayInterval = 5000,
  links = []
}) => {
  const [activeIndex, setActiveIndex] = useState(0);
  
  // 表示用の画像配列を作成（ループ用に前後に画像を追加）
  const displayImages = [...images.slice(-1), ...images, ...images.slice(0, 1)];
  
  // 元のリンク配列から実際の表示に対応するリンクを取得する関数
  const getSlideLink = (slideIndex: number, currentActiveIndex: number) => {
    // ループ用の画像を含む合計サイズ
    const totalDisplaySize = images.length + 2;
    
    // ループ用の先頭画像（最後の実画像に対応）
    if (slideIndex === 0) {
      return links.length > 0 ? links[links.length - 1] : '';
    }
    // メインの画像（本来の配列の要素）
    else if (slideIndex > 0 && slideIndex < totalDisplaySize - 1) {
      // 実際のリンク配列のインデックスを計算
      const actualLinkIndex = slideIndex - 1;
      return links.length > actualLinkIndex ? links[actualLinkIndex] : '';
    }
    // ループ用の末尾画像（最初の実画像に対応）
    else {
      return links.length > 0 ? links[0] : '';
    }
  };

  const handlePrevClick = () => {
    setActiveIndex(prev => {
      const newIndex = prev - 1;
      if (newIndex < 0) {
        setTimeout(() => {
          setActiveIndex(images.length - 1);
        }, 0);
        return 0;
      }
      return newIndex;
    });
  };

  const handleNextClick = () => {
    setActiveIndex(prev => {
      const newIndex = prev + 1;
      if (newIndex >= images.length) {
        setTimeout(() => {
          setActiveIndex(0);
        }, 0);
        return images.length - 1;
      }
      return newIndex;
    });
  };

  useEffect(() => {
    if (images.length <= 1) return;

    const interval = setInterval(() => {
      handleNextClick();
    }, autoPlayInterval);

    return () => clearInterval(interval);
  }, [images.length, autoPlayInterval]);

  return (
    <SliderContainer>
      <SliderWrapper>
        <SliderTrack activeIndex={activeIndex} totalImages={displayImages.length}>
          {displayImages.map((image, index) => {
            const isActive = index === activeIndex + 1;
            const link = getSlideLink(index, activeIndex);
            
            return (
              <SlideItemContainer key={index} isActive={isActive}>
                <ImageWrapper>
                  <AspectRatioBox>
                    {link ? (
                      <SlideItemLink 
                        to={link} 
                        $backgroundImage={image} 
                        $isActive={isActive}
                      >
                        <StyledLazyImage src={image} alt="スライド画像" lazy={false} />
                      </SlideItemLink>
                    ) : (
                      <SlideItemDiv 
                        $backgroundImage={image} 
                        $isActive={isActive}
                      >
                        <StyledLazyImage src={image} alt="スライド画像" lazy={false} />
                      </SlideItemDiv>
                    )}
                  </AspectRatioBox>
                </ImageWrapper>
              </SlideItemContainer>
            );
          })}
        </SliderTrack>
        <CustomNavigationButton className="prev" onClick={handlePrevClick}>
          ←
        </CustomNavigationButton>
        <CustomNavigationButton className="next" onClick={handleNextClick}>
          →
        </CustomNavigationButton>
      </SliderWrapper>
      {images.length > 1 && (
        <IndicatorContainer>
          {images.map((_, index) => (
            <Indicator
              key={index}
              active={index === activeIndex}
              onClick={() => setActiveIndex(index)}
            />
          ))}
        </IndicatorContainer>
      )}
    </SliderContainer>
  );
};

export default ImageSlider; 